import { useMemo } from 'react'

import {
  NAVIGATION_DESTINATIONS,
  type Destination,
} from '@/config/navigationDestinations'
import { useMyCapabilitySet } from '@/features/permissions/hooks'
import { useCanAdministerPermissions } from '@/hooks/useCanAdministerPermissions'
import { useConfiguration } from '@/hooks/useConfiguration'
import { useIsCareforceOrgAdmin } from '@/hooks/useIsCareforceOrgAdmin'
import { useIsGlobalAdmin } from '@/hooks/useIsGlobalAdmin'

/**
 * The destinations this user may actually reach, in registry order.
 *
 * Reads the capability set once rather than calling `useCapability` per row —
 * that would be a hook inside a loop over a list whose length changes with the
 * org. All the underlying queries are already cached by the sidebar and the
 * route guards, so this adds no request.
 *
 * Returns `[]` while capabilities are still resolving. Callers render the
 * open-tabs group immediately and let this group fill in; guessing would flash
 * rows the user cannot use.
 */
export function useNavigationDestinations(): Destination[] {
  const capabilities = useMyCapabilitySet()
  const { configuration, isPreviousOrgData } = useConfiguration()
  const isGlobalAdmin = useIsGlobalAdmin()
  const isCareforceOrgAdmin = useIsCareforceOrgAdmin()
  const canAdministerPermissions = useCanAdministerPermissions()

  // During an org switch the config query serves the previous org's data under
  // the new org's id, so trusting it would carry one org's rollout into
  // another — the same guard `useDutyMetricsEnabled` applies.
  const features = isPreviousOrgData ? undefined : configuration?.features

  return useMemo(() => {
    if (!capabilities) return []

    return NAVIGATION_DESTINATIONS.filter((destination) => {
      if (destination.capability && !capabilities.has(destination.capability)) {
        return false
      }
      if (
        destination.featureFlag &&
        !(features?.[destination.featureFlag] ?? false)
      ) {
        return false
      }
      switch (destination.role) {
        case 'globalAdmin':
          return isGlobalAdmin
        case 'careforceOrgAdmin':
          return isCareforceOrgAdmin
        case 'permissionsAdmin':
          return canAdministerPermissions
        default:
          return true
      }
    })
  }, [
    capabilities,
    features,
    isGlobalAdmin,
    isCareforceOrgAdmin,
    canAdministerPermissions,
  ])
}
